import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";

import { Input } from "../../components/Input";
import { useDarkMode } from "../../context/DarkModeContext";
import { Button } from "../../components/Button";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { changePassword } from "../../features/store/authSlice";
import { EyeIcon } from "../../assets/icons";

const schema = yup.object({
  oldPassword: yup.string().required("Old password is required"),
  newPassword: yup
    .string()
    .min(8, "New password must be at least 8 characters")
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const PasswordChange = () => {
  const { darkMode } = useDarkMode();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(schema) });

  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const onSubmit = async (data: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    try {
      await dispatch(changePassword(data)).unwrap();
      reset();
      toast.success("Password changed successfully!");
    } catch (error) {
      toast.error("Failed to change password. Please try again.");
    }
  };

  return (
    <div className="w-full md:w-[578px] md:h-[400px] p-6 font-satoshi bg-white dark:bg-black border border-[#EAECF0] dark:border-[#667085] rounded-lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm md:text-xl font-medium dark:text-white mb-4">
          Password Change
        </p>

        {/* Old Password */}
        <div>
          <Input
            type={showPassword.old ? "text" : "password"}
            placeholder="Old Password"
            register={register}
            name="oldPassword"
            required
            icon={
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({ ...prev, old: !prev.old }))
                }
              >
                {showPassword.old ? (
                  <EyeIcon theme={darkMode ? "dark" : "light"} />
                ) : (
                  <FaEyeSlash className="text-gray-400 dark:text-white" />
                )}
              </button>
            }
            className="dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
          />
          {errors.oldPassword && (
            <p className="text-red-500 text-xs">{errors.oldPassword.message}</p>
          )}
        </div>

        {/* New Password */}
        <div>
          <Input
            type={showPassword.new ? "text" : "password"}
            placeholder="Enter new password"
            register={register}
            name="newPassword"
            required
            icon={
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({ ...prev, new: !prev.new }))
                }
              >
                {showPassword.new ? (
                  <EyeIcon theme={darkMode ? "dark" : "light"} />
                ) : (
                  <FaEyeSlash className="text-gray-400 dark:text-white" />
                )}
              </button>
            }
            className="dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
          />
          {errors.newPassword && (
            <p className="text-red-500 text-xs">{errors.newPassword.message}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <Input
            type={showPassword.confirm ? "text" : "password"}
            placeholder="Repeat new Password"
            register={register}
            name="confirmPassword"
            required
            icon={
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    confirm: !prev.confirm,
                  }))
                }
              >
                {showPassword.confirm ? (
                  <EyeIcon theme={darkMode ? "dark" : "light"} />
                ) : (
                  <FaEyeSlash className="text-gray-400 dark:text-white" />
                )}
              </button>
            }
            className="dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Password Strength Indicator */}
        <PasswordStrengthIndicator password={watch("newPassword")} />

        <div className="flex justify-end">
          <Button
            type="submit"
            bgColor="bg-white"
            disabled={loading}
            className={`w-[142px] border border-[#EAECF0] dark:border-[#475467] dark:bg-[#FFFFFF1A] dark:text-white mt-4 py-2 rounded-lg text-sm ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }: { password: string }) => {
  const getStrength = () => {
    if (!password) return [false, false, false, false, false];

    return [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[0-9]/.test(password),
      /[^A-Za-z0-9]/.test(password),
      password.length >= 12, // Extra strength point for longer passwords
    ];
  };

  const strengthLevels = getStrength();

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-white">Password Strength</p>
      <div className="w-full h-1 flex">
        {strengthLevels.map((level, index) => (
          <div
            key={index}
            className={`flex-1 ${
              level ? "bg-green-500" : "bg-gray-200"
            } ml-0.5 ${index === 0 ? "rounded-l-full" : ""} ${
              index === 4 ? "rounded-r-full" : ""
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default PasswordChange;
